import type {
  AmenitiesSection,
  HealthSafetySecuritySection,
  HomeAutomationSection,
  LocationSection,
  MultimediaSection,
  RentalDescriptionSection,
  RentalSectionsStatus,
  SectionProgress,
  SubsectionStatus,
} from '@core/interfaces/rental-sections.interface';

import { mockDb } from '../mock-db';
import { getRentalContentLastUpdateMock } from './rental.functions';

/**
 * Generate mock sections status for a rental with configurable completion
 * @param rentalId - The rental identifier
 * @param completionPercentage - Overall completion percentage (0-100)
 * @returns Complete sections status object
 */
export function getMockRentalSectionsStatus(
  rentalId: string,
  completionPercentage = 65
): RentalSectionsStatus {
  const isComplete = (threshold: number) => completionPercentage >= threshold;

  const rentalDescription: RentalDescriptionSection = {
    maxSectionNumber: 4,
    progressSectionNumber: Math.min(4, Math.floor((completionPercentage / 100) * 4)),
    headlineDescription: { finished: isComplete(15) },
    theRental: { finished: isComplete(30) },
    howYourHost: { finished: isComplete(45) },
    policies: { finished: isComplete(60) },
  };

  const rentalMultimedia: MultimediaSection = {
    maxSectionNumber: 3,
    progressSectionNumber: Math.min(3, Math.floor((completionPercentage / 100) * 3)),
    spacesAndPhotos: { finished: isComplete(25) },
    rentalVideos: { finished: isComplete(75) },
    rentalVirtualTour: { finished: isComplete(90) },
  };

  const rentalLocation: LocationSection = {
    maxSectionNumber: 3,
    progressSectionNumber: Math.min(3, Math.floor((completionPercentage / 100) * 3)),
    mapAndAddressSettings: { finished: isComplete(20) },
    locationAndView: { finished: isComplete(50) },
    localAttractionsAndActivities: { finished: isComplete(80) },
  };

  const rentalAmenities: AmenitiesSection = {
    maxSectionNumber: 6,
    progressSectionNumber: Math.min(6, Math.floor((completionPercentage / 100) * 6)),
    parkingAndStorage: { finished: isComplete(35) },
    climate: { finished: isComplete(40) },
    entertainmentAndMultimedia: { finished: isComplete(55) },
    kitchenAndDining: { finished: isComplete(65) },
    bathroomAndLaundry: { finished: isComplete(70) },
    otherService: { finished: isComplete(85) },
  };

  const rentalHealthSafetySecurity: HealthSafetySecuritySection = {
    maxSectionNumber: 5,
    progressSectionNumber: Math.min(5, Math.floor((completionPercentage / 100) * 5)),
    securityServices: { finished: isComplete(50) },
    safetyEquipment: { finished: isComplete(60) },
    healthEquipmentAndAmenities: { finished: isComplete(70) },
    emergencyInfo: { finished: isComplete(75) },
    cleaningServices: { finished: isComplete(80) },
  };

  const rentalHomeAutomation: HomeAutomationSection = {
    maxSectionNumber: 4,
    progressSectionNumber: Math.min(4, Math.floor((completionPercentage / 100) * 4)),
    assignGateway: { finished: isComplete(85) },
    automationRules: { finished: isComplete(90) },
    alarm: { finished: isComplete(95) },
    rentsodyLock: { finished: isComplete(100) },
  };

  return {
    rentalDescription,
    rentalMultimedia,
    rentalLocation,
    rentalAmenities,
    rentalHealthSafetySecurity,
    rentalHomeAutomation,
  };
}

export const MOCK_RENTAL_IDS = {
  COMPLETE: 'beach-apartment-marbella',
  MOSTLY_COMPLETE: 'mountain-chalet-sierra-nevada',
  HALF_COMPLETE: 'city-loft-barcelona',
  JUST_STARTED: 'countryside-villa-andalucia',
  MINIMAL: 'studio-apartment-madrid',

  EMPTY: 'penthouse-malaga-new',
  ONLY_DESCRIPTION: 'beachhouse-cadiz-draft',
  NO_MULTIMEDIA: 'farmhouse-granada-no-photos',
  NO_AUTOMATION: 'traditional-cortijo-almeria',

  NOT_FOUND: 'deleted-property-404',
  UNAUTHORIZED: 'expired-session-villa',
  FORBIDDEN: 'private-estate-sotogrande',
  SERVER_ERROR: 'server-down-apartment',
  TIMEOUT: 'slow-connection-chalet',
  INVALID_DATA: 'corrupted-data-loft',
} as const;

/**
 * Check if a rental ID should trigger an error response
 * Returns error details if it should error, null otherwise
 */
export function getErrorScenario(rentalId: string): { status: number; message: string } | null {
  switch (rentalId) {
    case MOCK_RENTAL_IDS.NOT_FOUND:
      return {
        status: 404,
        message: 'Property not found. This listing may have been deleted or never existed.',
      };
    case MOCK_RENTAL_IDS.UNAUTHORIZED:
      return { status: 401, message: 'Your session has expired. Please login again to continue.' };
    case MOCK_RENTAL_IDS.FORBIDDEN:
      return {
        status: 403,
        message: 'Access denied. You do not have permission to view this property.',
      };
    case MOCK_RENTAL_IDS.SERVER_ERROR:
      return {
        status: 500,
        message: 'Server error. Our team has been notified. Please try again later.',
      };
    case MOCK_RENTAL_IDS.TIMEOUT:
      return {
        status: 408,
        message: 'Request timeout. Please check your connection and try again.',
      };
    case MOCK_RENTAL_IDS.INVALID_DATA:
      return {
        status: 422,
        message: 'Invalid property data. Please contact support if this issue persists.',
      };
    default:
      return null;
  }
}

/**
 * Get sections status by rental ID from predefined mock states
 * Maps specific rental IDs to different completion states for testing
 * @param rentalId - The rental identifier
 * @returns Sections status for the given rental
 */
export function getRentalSectionsByIdMock(rentalId: string): RentalSectionsStatus {
  const mockStates: Record<string, number> = {
    [MOCK_RENTAL_IDS.COMPLETE]: 100,
    [MOCK_RENTAL_IDS.MOSTLY_COMPLETE]: 75,
    [MOCK_RENTAL_IDS.HALF_COMPLETE]: 50,
    [MOCK_RENTAL_IDS.JUST_STARTED]: 25,
    [MOCK_RENTAL_IDS.MINIMAL]: 10,
    [MOCK_RENTAL_IDS.EMPTY]: 0,
    [MOCK_RENTAL_IDS.ONLY_DESCRIPTION]: 15,
    [MOCK_RENTAL_IDS.NO_MULTIMEDIA]: 85,
    [MOCK_RENTAL_IDS.NO_AUTOMATION]: 80,
    'rental-001': 75,
    'rental-002': 50,
    'rental-003': 20,
  };

  const completion = mockStates[rentalId] ?? 65;

  return withRealContentSubsections(rentalId, getMockRentalSectionsStatus(rentalId, completion));
}

/**
 * Overlays two subsections onto a generated status: `rentalDescription.faqs` (finished = at least
 * one FAQ regardless of `enabled`) and `rentalLocation.locationAndView` (finished = non-empty
 * `locationText`), both derived from the mock DB rental the PATCH mock persists into, with the
 * progress counters recomputed to match.
 */
function withRealContentSubsections(
  rentalId: string,
  status: RentalSectionsStatus
): RentalSectionsStatus {
  const rental = mockDb.getRentalById(rentalId);
  const stamps = getRentalContentLastUpdateMock(rentalId);

  const faqsFinished = !!rental?.faqs?.some(
    faq => faq.question.trim().length > 0 && faq.answer.trim().length > 0
  );
  const locationFinished = !!rental?.locationText?.trim();

  const rentalDescription = {
    ...status.rentalDescription,
    maxSectionNumber: 5,
    faqs: { finished: faqsFinished, lastUpdate: stamps.faqs ?? null },
  };
  rentalDescription.progressSectionNumber = countFinished(rentalDescription);

  const rentalLocation = {
    ...status.rentalLocation,
    locationAndView: { finished: locationFinished, lastUpdate: stamps.locationText ?? null },
  };
  rentalLocation.progressSectionNumber = countFinished(rentalLocation);

  return { ...status, rentalDescription, rentalLocation };
}

function countFinished(section: SectionProgress & Record<string, unknown>): number {
  return Object.values(section).filter(
    (value): value is SubsectionStatus =>
      typeof value === 'object' &&
      value !== null &&
      'finished' in value &&
      (value as SubsectionStatus).finished
  ).length;
}

/**
 * Create a fully completed sections status (100%)
 * Useful for testing the "completed" state
 */
export function getFullyCompletedSectionsMock(rentalId: string): RentalSectionsStatus {
  return getMockRentalSectionsStatus(rentalId, 100);
}

/**
 * Create an empty sections status (0%)
 * Useful for testing the "empty" state
 */
export function getEmptySectionsMock(rentalId: string): RentalSectionsStatus {
  return getMockRentalSectionsStatus(rentalId, 0);
}

/**
 * Create a sections status with only description complete
 * Simulates a newly created rental where only basic info is added
 */
export function getDescriptionOnlySectionsMock(rentalId: string): RentalSectionsStatus {
  const status = getMockRentalSectionsStatus(rentalId, 0);

  status.rentalDescription = {
    maxSectionNumber: 4,
    progressSectionNumber: 4,
    headlineDescription: { finished: true },
    theRental: { finished: true },
    howYourHost: { finished: true },
    policies: { finished: true },
  };

  return status;
}

/**
 * Create a sections status with everything except multimedia
 * Simulates a rental that's almost ready but needs photos/videos
 */
export function getNoMultimediaSectionsMock(rentalId: string): RentalSectionsStatus {
  const status = getMockRentalSectionsStatus(rentalId, 100);

  status.rentalMultimedia = {
    maxSectionNumber: 3,
    progressSectionNumber: 0,
    spacesAndPhotos: { finished: false },
    rentalVideos: { finished: false },
    rentalVirtualTour: { finished: false },
  };

  return status;
}

/**
 * Create a sections status with everything except home automation
 * Simulates a traditional rental without smart home features
 */
export function getNoAutomationSectionsMock(rentalId: string): RentalSectionsStatus {
  const status = getMockRentalSectionsStatus(rentalId, 100);

  status.rentalHomeAutomation = {
    maxSectionNumber: 4,
    progressSectionNumber: 0,
    assignGateway: { finished: false },
    automationRules: { finished: false },
    alarm: { finished: false },
    rentsodyLock: { finished: false },
  };

  return status;
}

/**
 * Create a sections status with random completion
 * Simulates a work-in-progress rental with scattered completions
 */
export function getRandomSectionsMock(rentalId: string): RentalSectionsStatus {
  const seed = rentalId.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const random = (min: number, max: number) => {
    const x = Math.sin(seed) * 10000;
    const normalized = (x - Math.floor(x)) * (max - min) + min;
    return Math.floor(normalized);
  };

  return getMockRentalSectionsStatus(rentalId, random(10, 90));
}
